import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isObject from 'lodash/isObject';
import _isNumber from 'lodash/isNumber';
import _isString from 'lodash/isString';
import _isBoolean from 'lodash/isBoolean';
import _isNil from 'lodash/isNil';
import jmesPath from 'jmespath';
import {
  ParserSupportedFhirTypes,
  ParserConfig,
  MomentFormat,
  FormatMessageSignature,
  JmesPath,
} from './jmesFhirTypes';
import {
  BaseParserConstructor,
  BaseParserOptions,
} from '../../util/fhirResourceParsers/SharedTypes';
import { FhirTypeParserMap, fhirTypeParserMap } from './util';

export type ParserFhirTypeSelectOption = {
  value: ParserSupportedFhirTypes;
  display: string;
};

export default class JMESParser {
  static supportedTypes: FhirTypeParserMap = fhirTypeParserMap;
  private _jmesSearchInput: any;
  private _jmesSearchPath: JmesPath;
  private _outputType?: ParserSupportedFhirTypes;
  private _momentFormat?: MomentFormat;
  private _formatMessage?: FormatMessageSignature;

  constructor(
    jmesSearchInput: any,
    jmesSearchPath: JmesPath,
    outputType?: ParserSupportedFhirTypes,
    momentFormat?: MomentFormat,
    formatMessage?: FormatMessageSignature,
  ) {
    this._jmesSearchInput = jmesSearchInput;
    this._jmesSearchPath = jmesSearchPath;
    this._outputType = outputType;
    this._momentFormat = momentFormat;
    this._formatMessage = formatMessage;
  }

  get jmesSearchInput() {
    return this._jmesSearchInput;
  }

  get jmesSearchPath() {
    return this._jmesSearchPath;
  }

  get outputType() {
    return this._outputType;
  }

  get momentFormat() {
    return this._momentFormat;
  }

  get formatMessage() {
    return this._formatMessage;
  }

  // parses the jmes value without applying the type parser to the result
  untypedParse(nullOnError = false): any {
    try {
      return jmesPath.search(this._jmesSearchInput, this._jmesSearchPath);
    } catch (e) {
      return nullOnError
        ? null
        : // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          this._formatMessage({
            id: 'patientViewer.header.jmesPath.error',
            defaultMessage: 'Error Parsing Field',
          });
    }
  }

  // parses the jmes value and applies the type parser to the result
  parse(): string {
    try {
      const jmesSearchResult = this.untypedParse(true);
      const outputTypeConfig: ParserConfig = _get<
        FhirTypeParserMap,
        ParserSupportedFhirTypes,
        ParserConfig
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      >(JMESParser.supportedTypes, this._outputType, null);
      const OutputType: BaseParserConstructor = _get<
        ParserConfig,
        'Parser',
        BaseParserConstructor
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      >(outputTypeConfig, 'Parser', null);

      if (OutputType) {
        const dateTimeOptions: BaseParserOptions = {
          fhirDateTime: {
            monthFormat: this._momentFormat,
            dayFormat: this._momentFormat,
            minuteFormat: this._momentFormat,
          },
          address: {
            omitLineBreaks: true,
          },
        };

        const parser = new OutputType(dateTimeOptions);

        if (_isArray(jmesSearchResult)) {
          // if jmes parsing returns an array, apply type parser to each array raw value
          return jmesSearchResult
            .map(individualResult => parser.getDisplayValue(individualResult))
            .filter(Boolean)
            .join(', ');
        } else {
          return parser.getDisplayValue(jmesSearchResult);
        }
      } else {
        return jmesSearchResult;
      }
    } catch (e) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return this._formatMessage({
        id: 'patientViewer.header.jmesPath.error',
        defaultMessage: 'Error Parsing Field',
      });
    }
  }

  /**
   * Parses the value into something that can be rendered
   */
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  parseToRenderableValue(fallbackValue: string = null): string {
    const parseResult = this.parse();

    // if no result or result is object (but not an array)
    if (
      _isNil(parseResult) ||
      (_isObject(parseResult) && !_isArray(parseResult))
    ) {
      return fallbackValue;
    }

    const isDisplayableValue = (val: any) =>
      _isString(val) || _isNumber(val) || _isBoolean(val);

    if (isDisplayableValue(parseResult)) {
      return String(parseResult);
    } else if (_isArray(parseResult)) {
      // unable to display arrays, but if all array values can each be displayed, join with a comma
      const consistsOfDisplayValues = parseResult.every(result =>
        isDisplayableValue(result),
      );

      if (consistsOfDisplayValues) {
        return parseResult
          .map(parsedResultPart => String(parsedResultPart))
          .join(', ');
      } else {
        return fallbackValue;
      }
    } else {
      return fallbackValue;
    }
  }
}
